/**
 * WebhookChannelsCard Component
 *
 * Custom webhook notification channels card for settings page.
 * Displays existing webhooks and provides button to add new ones via App Bridge modal.
 *
 * Design Compliance:
 * - Single Card pattern (matches other settings cards)
 * - Icon + Title header with tooltip
 * - Uses Polaris spacing scale (gap="400", gap="500")
 * - Consistent channel item treatment using ChannelItem component
 */

import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Box,
  Icon,
  Collapsible,
} from '@shopify/polaris';
import { CodeIcon, ChevronUpIcon, ChevronDownIcon, PlusCircleIcon } from '@shopify/polaris-icons';
import { ChannelItem } from '../channel-item';
import { EmptyStateIllustration } from '../../ui/EmptyStateIllustration';
import { EmptyStateContainer } from '../../ui/EmptyStateContainer';
import type { NotificationChannel } from '../../dashboard/NotificationChannels';

export interface WebhookChannelsCardProps {
  /** Webhook notification channels */
  channels: NotificationChannel[];
  /** Handler for testing a channel */
  onChannelTest: (channel: NotificationChannel) => void;
  /** Handler for toggling a channel */
  onChannelToggle: (channel: NotificationChannel) => void;
  /** Handler for deleting a channel */
  onChannelDelete: (channel: NotificationChannel) => void;
  /** Handler for clicking "Add webhook" button - opens modal via parent */
  onAddWebhookClick: () => void;
  /** Callback when copy-to-clipboard succeeds */
  onCopySuccess?: (message: string) => void;
  /** Whether actions are loading */
  isLoading?: boolean;
  /** Whether this section is collapsible */
  isCollapsible?: boolean;
  /** Whether the section is open (only used when isCollapsible is true) */
  isOpen?: boolean;
  /** Handler for toggling the section (only used when isCollapsible is true) */
  onToggle?: () => void;
}

export function WebhookChannelsCard({
  channels,
  onChannelTest,
  onChannelToggle,
  onChannelDelete,
  onAddWebhookClick,
  onCopySuccess,
  isLoading = false,
  isCollapsible = false,
  isOpen = true,
  onToggle,
}: WebhookChannelsCardProps) {
  const content = (
    <Box paddingBlockStart="200">
      <BlockStack gap="400">
        {channels.length === 0 ? (
          <Box
            background="bg-surface-secondary"
            padding="400"
            borderRadius="200"
          >
            <EmptyStateContainer size="sm">
              <BlockStack gap="200" align="center">
                <EmptyStateIllustration variant="webhooks" width={180} height={144} />
                <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                  No webhooks configured
                </Text>
                <InlineStack align="center">
                  <Button size="slim" icon={PlusCircleIcon} onClick={onAddWebhookClick}>
                    Add webhook
                  </Button>
                </InlineStack>
              </BlockStack>
            </EmptyStateContainer>
          </Box>
        ) : (
          <BlockStack gap="300">
            {channels.map((channel) => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                onTest={onChannelTest}
                onToggle={onChannelToggle}
                onDelete={onChannelDelete}
                onCopySuccess={onCopySuccess}
                isLoading={isLoading}
              />
            ))}
            <InlineStack align="start">
              <Button size="slim" icon={PlusCircleIcon} onClick={onAddWebhookClick}>
                Add webhook
              </Button>
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>
    </Box>
  );

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        {isCollapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls="section-webhook-channels"
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <InlineStack align="space-between" blockAlign="start">
              <InlineStack gap="200" blockAlign="start">
                <Box paddingBlockStart="050">
                  <Icon source={CodeIcon} tone="subdued" />
                </Box>
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Webhooks
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Integrate with custom systems via HTTP
                  </Text>
                </BlockStack>
              </InlineStack>
              <Box paddingBlockStart="050">
                <Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} tone="subdued" />
              </Box>
            </InlineStack>
          </button>
        ) : (
          <InlineStack align="space-between" blockAlign="start">
            <InlineStack gap="200" blockAlign="start">
              <Box paddingBlockStart="050">
                <Icon source={CodeIcon} tone="subdued" />
              </Box>
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Webhooks
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Integrate with custom systems via HTTP
                </Text>
              </BlockStack>
            </InlineStack>
          </InlineStack>
        )}

        {/* Content */}
        {isCollapsible ? (
          <Collapsible
            open={isOpen}
            id="section-webhook-channels"
            transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
          >
            {content}
          </Collapsible>
        ) : (
          content
        )}
      </BlockStack>
    </Card>
  );
}
